// 패치노트 — 상단 바 [패치노트] 버튼이 보여 주는 일자별 업데이트 내역(2026-10-19 오너 요청:
// "패치할때마다 패치노트를 볼 수 있게 … 신규 업데이트가 있으면 깜빡이는 효과까지").
//
// 쓰는 법
//   · 배포할 때마다 **맨 위에** 그날 항목을 더한다(같은 날 두 번 내면 그날 항목에 덧붙인다).
//   · 쓰는 분이 체감하는 변경만 쓴다 — 새 기능 · 화면 · 고침. 배포 도구 · 시험 · 문서 같은 내부 작업은 뺀다.
//   · 말은 쉬운 한국어로, 화면에 보이는 이름(메뉴 · 버튼)을 그대로 쓴다.
//   · 종류(새 기능 · 화면 개선 · 고침)마다 콘솔 전체에 걸친 공통 사항 → 주요 사항 → 자잘한 사항 순으로 놓는다
//     (2026-10-27 오너 "파트별로 공통 적용사항 - 주요 적용사항 - 마이너한 적용사항 순으로").
//   · 항목마다 어느 파트의 변경인지 part 를 단다 — 콘솔 전체면 common(공통), 그 밖에는 business(사업) ·
//     manufacturing(제조) · field(필드). 글 앞에 사이드바 묶음 색 배지로 보인다(글에 [필드] 를 쓰지 않는다)
//     (2026-11-02 오너 "공통적으로 적용되는 사안이면 앞에 [공통]을 붙히고, 필드쪽에 적용되는 사안이면 [필드]").
// 맨 위 항목이 바뀌면 [패치노트] 버튼이 깜빡인다(본 적 있는 판은 브라우저에 적어 둔다).

import type { IconName } from '@/components/Icon';

export type PatchNoteKind = 'new' | 'ui' | 'fix';
/**
 * 어느 파트의 변경인가(2026-11-02 오너 — "[공통] · [필드] 말머리 … 색으로 구분해서"). 사이드바 묶음과 같은 색 배지로
 * 글 앞에 붙는다. 콘솔 전체나 여러 파트에 걸친 것은 common.
 */
export type PatchNotePart = 'common' | 'business' | 'manufacturing' | 'field';
export type PatchNoteApp = 'console' | 'field';

export interface PatchNoteItem {
  kind: PatchNoteKind;
  /** 줄 앞 아이콘. 비우면 글에 나온 말(지도 · 계약 · 로봇 …)로 고르고, 그것도 없으면 종류별 기본. */
  icon?: IconName;
  /** 어느 앱의 변경인가 — 콘솔(기본) · 현장 앱(setup.beyondhoneycomb.com). */
  app?: PatchNoteApp;
  /** 파트 말머리 — 배지로 보인다. */
  part: PatchNotePart;
  text: string;
}

export interface PatchNoteDay {
  /** 한국 날짜 YYYY-MM-DD */
  date: string;
  items: PatchNoteItem[];
}

export const PATCH_NOTE_PART_LABEL: Record<PatchNotePart, string> = {
  common: '공통',
  business: '사업',
  manufacturing: '제조',
  field: '필드',
};

export const PATCH_NOTE_KIND_LABEL: Record<PatchNoteKind, string> = {
  new: '새 기능',
  ui: '화면 개선',
  fix: '고침',
};

export const PATCH_NOTES: PatchNoteDay[] = [
  {
    date: '2026-11-04',
    items: [
      {
        kind: 'new',
        part: 'manufacturing',
        text: '로봇 배정 화면에 맞는 로봇으로 채우기 버튼이 생겼습니다. 누르면 희망 사양에 맞는 창고 로봇이 빈자리에 들어가고, 맞는 재고가 없으면 설치일 전에 나오는 제조 계획이 들어갑니다.',
      },
      {
        kind: 'new',
        part: 'field',
        text: '통합 캘린더에서 유지보수 일정을 추가해 생긴 고객사 리포트는 구분이 필드팀 요청으로 들어가고, 구글 시트의 조치결과 칸에도 같은 내용이 적힙니다.',
      },
      {
        kind: 'ui',
        part: 'manufacturing',
        text: '로봇 배정 화면을 새로 꾸몄습니다. 위에서 고객사나 이벤트를 고르면 필요한 대수만큼 로봇 자리가 서고, 왼쪽 창고 로봇을 끌어다 놓으면 그 자리에 들어갑니다. 로봇은 누르기만 해도 여러 대를 고를 수 있고, 목록의 빈 곳에서 끌어 한꺼번에 고를 수도 있습니다.',
      },
      {
        kind: 'ui',
        part: 'manufacturing',
        text: '로봇 배정 화면의 계약 줄마다 위에 정방향, 역방향, 방향 미정, 제퍼가 크게 적혀 어떤 로봇을 넣을 자리인지 바로 알 수 있습니다.',
      },
      {
        kind: 'ui',
        part: 'manufacturing',
        text: '로봇 배정 화면에서 고객사 이름을 누르면 그 고객사의 상세 화면으로 넘어갑니다.',
      },
      {
        kind: 'ui',
        part: 'manufacturing',
        text: '로봇 배정 화면의 창고 로봇이 뒤섞이지 않고 로봇 번호순으로 보입니다.',
      },
      {
        kind: 'ui',
        part: 'manufacturing',
        text: '로봇 상세의 부품 장착과 ECN 적용 목록도 Ctrl 없이 누르기만 해서 여러 개를 고를 수 있습니다. 다시 누르면 빠집니다.',
      },
      {
        kind: 'fix',
        part: 'common',
        text: '슬랙의 로봇 배정, 설치 완료, 회수, 교체 알림에서 로봇이 알아볼 수 없는 글자로 찍히던 것을 고쳤습니다. 이제 #124-2처럼 Short SN으로만 보이고, Short SN이 없는 로봇은 Short SN 미등록으로 표시됩니다.',
      },
    ],
  },
  {
    date: '2026-11-03',
    items: [
      {
        kind: 'new',
        part: 'field',
        text: '통합 캘린더에서 유지보수 일정을 추가할 때 그 고객사의 처리되지 않은 고객사 리포트가 함께 보입니다. 같은 건이면 기존 리포트에 이어서 리포트 번호를 하나로 두고, 다른 건이면 새 리포트로 등록할 수 있습니다.',
      },
      {
        kind: 'new',
        part: 'field',
        text: '고객사 리포트 수정 창에서 같은 건으로 합치기를 누르면 같은 신고로 두 개가 된 리포트를 하나로 합칠 수 있습니다. 먼저 등록된 번호가 남고, 방문 일정과 조치 이력도 남는 리포트로 옮겨집니다.',
      },
      {
        kind: 'new',
        part: 'field',
        text: '고객사 리포트에서 고친 요약과 상태가 1분 안에 구글 시트의 작성 내용과 콘솔상태 칸에도 반영되고, 시트에서 고친 내용도 콘솔로 들어옵니다. 같은 칸을 양쪽에서 고치면 나중에 고친 내용이 남고, 캘린더 유지보수로 만든 리포트는 시트 맨 아래에 새 줄로 추가됩니다.',
      },
      {
        kind: 'new',
        part: 'manufacturing',
        text: '로봇 상세의 최근 이력에서 시점으로 되돌리면 그 뒤에 자동으로 만들어진 회수 정비 서비스 오더도 함께 취소되고, 되돌리기를 취소하면 다시 열립니다.',
      },
      {
        kind: 'ui',
        part: 'manufacturing',
        text: '재고 현황을 새로 꾸몄습니다. 모델과 사양마다 출고할 수 있는 대수를 큰 숫자로 보여 주고, 사양을 누르면 출고 가능, 출고 확정, 공정 진행 중인 로봇 번호가 펼쳐집니다. 출고됐다가 다시 들어온 로봇에는 재입고라고 표시됩니다.',
      },
      {
        kind: 'ui',
        part: 'field',
        text: '고객사 리포트 표의 상태 아래에 이어진 방문 일정의 날짜가 보입니다.',
      },
      {
        kind: 'ui',
        part: 'manufacturing',
        text: '로더 이력의 검색창 폭을 줄이고 업체 품질과 개조 관리 버튼을 제목 옆으로 옮겼습니다.',
      },
      {
        kind: 'fix',
        part: 'manufacturing',
        text: '재고 현황에서 색상이나 방향 같은 사양이 비어 있는 로봇은 어떤 값이 빠졌는지 미입력으로 알려 주도록 고쳤습니다.',
      },
    ],
  },
  {
    date: '2026-11-02',
    items: [
      {
        kind: 'new',
        part: 'manufacturing',
        text: '로봇 상세의 최근 이력에서 편집을 누르면 이력을 고칠 수 있습니다. 줄을 누르면 그 시점으로 되돌려 그 뒤의 이력을 숨기고 로봇의 상태와 위치, 출고 기록도 그때 모습으로 돌립니다. 지우기를 누르면 그 줄만 숨기고, 되돌리기 취소와 다시 살리기로 원래대로 돌릴 수 있습니다.',
      },
      {
        kind: 'new',
        part: 'field',
        text: '통합 캘린더에서 추가한 유지보수 일정은 이제 고장 조치 이력이 아니라 고객사 리포트에 올라가고, 리포트 번호도 방문일에 맞춰 자동으로 매겨집니다. 지금까지 캘린더로 올라갔던 건도 고객사 리포트로 옮겼고, 캘린더에서 일정을 누르면 그 리포트로 바로 갑니다.',
      },
      {
        kind: 'new',
        part: 'field',
        text: '통합 캘린더에 이벤트 일정을 추가할 수 있습니다. 설치와 회수 일시, 설치 장소, 이벤트 내용, 출고할 로봇, 준비물을 적으면 캘린더와 구글 캘린더에 이벤트 설치와 이벤트 회수 일정이 따로 올라가고, 고른 준비물은 일정 메모에 적힙니다.',
      },
      {
        kind: 'new',
        part: 'field',
        text: '이벤트 설치 일정을 우클릭해 설치 완료를 누르면 배정된 로봇이 로봇 배치의 행사·전시처럼 출고 이력에 남고, 회수 완료를 누르면 반납으로 기록됩니다. 두 가지 모두 우클릭으로 되돌릴 수 있습니다.',
      },
      {
        kind: 'new',
        part: 'field',
        text: '이벤트 일정에 이벤트 유형과 비고를 적을 수 있습니다. 설치 장소는 네이버 지도에서 찾아 지도 링크를 붙일 수 있고, 링크를 비워 두면 구글 캘린더 메모에 장소 검색 링크가 들어갑니다.',
      },
      {
        kind: 'new',
        part: 'manufacturing',
        text: '로봇 배정 화면에서 이벤트에 나갈 로봇을 미리 배정할 수 있습니다. 배정한 로봇은 이벤트 회수를 마칠 때까지 계약 배정 후보에서 빠지고, 캘린더에서 회수 완료를 누르면 정비 상태로 들어갑니다.',
      },
      {
        kind: 'new',
        part: 'field',
        text: '통합 캘린더에서 유지보수 일정을 추가할 때 작업 메모와 따로 증상을 적을 수 있습니다. 적은 증상은 고객사 리포트의 요약으로 올라갑니다.',
      },
      {
        kind: 'ui',
        part: 'common',
        text: '패치노트의 항목마다 공통, 사업, 제조, 필드 중 어느 파트의 변경인지 색 배지로 보여 줍니다. 색은 왼쪽 메뉴 묶음의 색과 같습니다.',
      },
      {
        kind: 'ui',
        part: 'common',
        text: '더 보기(⋯) 메뉴에서 마우스를 올린 항목에 색이 들어와 지금 무엇을 누를지 알아보기 쉬워졌습니다.',
      },
      {
        kind: 'ui',
        part: 'manufacturing',
        text: '로봇 상세 화면을 읽기 쉽게 새로 꾸몄습니다. 맨 위에 로봇 번호와 지금 상태, 있는 곳, QR을 명판처럼 크게 보여 주고, 자주 쓰지 않는 동작은 더 보기(⋯) 메뉴로 모았습니다.',
      },
      {
        kind: 'ui',
        part: 'manufacturing',
        text: '로봇 상세의 출고 이력, 제조 이력, 고장 이력, 펌웨어, 부품, 로더, 교차 참조, QR 스티커 탭도 개요와 같은 모양으로 맞췄습니다. 비어 있는 값은 없음으로 보이고, 영문 코드로 보이던 상태와 검증 상태도 한국어로 바꿨습니다.',
      },
      {
        kind: 'ui',
        part: 'field',
        text: '고객사 리포트 표의 조치결과 칸 이름을 구분으로 바꾸고, 등급 옆에 있던 문제 구분 칸은 뺐습니다.',
      },
      {
        kind: 'fix',
        part: 'field',
        text: '고객사 리포트의 요약에 적은 줄바꿈이 표에서도 그대로 보이게 고쳤습니다.',
      },
    ],
  },
  {
    date: '2026-10-28',
    items: [
      {
        kind: 'new',
        part: 'field',
        text: '고장 조치 이력에 조치 후 원인 파악 중 상태가 생겼습니다. 조치는 했지만 원인을 아직 찾고 있는 건을 따로 둘 수 있고, 노션에서 옮겨 온 미해결 건은 이 상태로 들어갑니다.',
      },
      {
        kind: 'new',
        part: 'field',
        text: '필드 대시보드의 신규 이슈와 주요 누적 이슈는 오른쪽 위 펜 단추로 글을 직접 고칠 수 있습니다. 고친 글은 그 기간에만 남고, 자동 글로 언제든 되돌릴 수 있습니다.',
      },
      {
        kind: 'new',
        part: 'field',
        text: '새 조치 이력을 쓸 때 매장 이름을 치면 등록된 매장을 추천하고, 고른 매장에 있는 로봇을 체크해서 여러 대를 한 번에 고를 수 있습니다.',
      },
      {
        kind: 'new',
        part: 'field',
        text: '새 조치 이력에는 작성 일시 대신 문제 발생일을 고릅니다. 리포트 번호는 그날의 다음 번호로 자동으로 채워지고, 작성 일시는 저장할 때 자동으로 남습니다.',
      },
      {
        kind: 'new',
        part: 'field',
        text: '고객사 리포트에 로봇 이슈인지 로봇 외 이슈인지 보여 주는 구분 칸이 생겼고, 접수한 달과 반기로 걸러 볼 수 있습니다.',
      },
      {
        kind: 'new',
        part: 'field',
        text: '고객사 리포트의 리포트 번호를 누르면 그 번호의 조치 이력만 걸러서 열립니다.',
      },
      {
        kind: 'new',
        part: 'manufacturing',
        text: '이미 고객사에 배치된 로봇을 로봇 상세에서 바로 스페어로 전환하거나 해제할 수 있습니다. 배치는 그대로 두고 설치 대수에서만 빠집니다.',
      },
      {
        kind: 'new',
        part: 'field',
        text: '서로 다른 조치 이력이 같은 리포트 번호를 쓰면 고장 조치 이력 위에 알림이 뜨고 그 줄에 번호 겹침이 표시됩니다. 새 조치 이력에 이미 쓴 번호를 적어도 바로 알려 줍니다.',
      },
      {
        kind: 'new',
        part: 'field',
        text: '고장 조치 이력을 30건씩 나눠 보여 주고 아래에서 쪽을 넘길 수 있습니다. 전에는 앞의 50건만 보였습니다.',
      },
      {
        kind: 'new',
        part: 'field',
        text: '고장 조치 이력 표의 상태, 구분, 리포트 번호, 매장, 로봇, 오류 코드, 발생 일시 머리를 누르면 로봇 목록처럼 그 칸으로 정렬됩니다.',
      },
      {
        kind: 'new',
        part: 'manufacturing',
        text: '로봇 상세의 부품 탭에서 왼쪽 목록의 부품을 로봇 그림에 끌어다 놓으면 장착됩니다. 로봇 그림은 GEN1, GEN2, 제퍼 모델마다 다르게 나오고, 이미 장착된 부품은 목록에 장착됨과 슬롯이 함께 보입니다. Ctrl을 누른 채 클릭하거나 빈 곳에서 끌어 여러 부품을 한 번에 고를 수 있고, 놓은 뒤에는 슬롯과 LOT만 확인하고 장착하면 됩니다.',
      },
      {
        kind: 'new',
        part: 'manufacturing',
        text: '부품 탭의 부품 목록 위에 부품 등록 버튼이 생겨, 목록에 없는 부품을 그 자리에서 새로 등록하고 바로 장착할 수 있습니다.',
      },
      {
        kind: 'new',
        part: 'manufacturing',
        text: '부품 탭의 적용 변경점도 부품처럼 왼쪽 목록의 변경점을 로봇 그림에 끌어다 놓아 적용합니다. 이미 적용된 변경점은 목록에 적용일과 함께 표시되고, 목록 위의 변경점 추가 버튼으로 새 변경점을 바로 만들 수 있습니다.',
      },
      {
        kind: 'ui',
        part: 'common',
        text: '왼쪽 메뉴의 BUSINESS, MANUFACTURING, FIELD 이름을 눌러야 열리던 대시보드를 각 묶음 맨 위의 사업 대시보드, 제조 대시보드, 필드 대시보드 항목으로 따로 꺼냈습니다.',
      },
      {
        kind: 'ui',
        part: 'common',
        text: '구획 테두리는 또렷하게 두고, 표 안의 줄과 카드 테두리는 연하게 바꿔 화면이 덜 복잡해 보이게 했습니다.',
      },
      {
        kind: 'ui',
        part: 'manufacturing',
        text: '부품 탭의 부품 장착 칸은 처음에 접혀 있고, 아래 초록 막대를 누르면 펼쳐집니다. 적용 변경점 칸은 늘 펼쳐져 있습니다.',
      },
      {
        kind: 'ui',
        part: 'field',
        text: '필드 대시보드의 로봇 운영 이슈 그래프마다 크게 보기 단추가 생겼습니다. 26.10 상반기 조치 이력 보기를 누르면 대시보드가 센 그 기간의 이슈만 걸러서 열립니다.',
      },
      {
        kind: 'ui',
        part: 'field',
        text: '필드 대시보드에서 30일 내 설치 예정이 위로 올라왔고, 심각도별 미해결 리포트와 고장 조치 이력은 한 줄에 나란히 보입니다.',
      },
      {
        kind: 'ui',
        part: 'field',
        text: '30일 내 설치 예정 표는 따로 있던 상세 칸을 없애고, 줄을 누르면 고객사 상세가 열립니다.',
      },
      {
        kind: 'ui',
        part: 'business',
        text: '고객사 상세의 로봇 정보에는 지금 나가 있는 로봇만 보이고, 회수된 기록은 아래에 접어 두었습니다. 배치 되돌리기와 스페어 전환은 줄 끝 메뉴로 옮겼습니다.',
      },
      {
        kind: 'ui',
        part: 'field',
        text: '로봇 운영 이슈 그래프에서 선을 고르면 아래 요약 표의 같은 줄이 그 선 색으로 칠해집니다.',
      },
      {
        kind: 'ui',
        part: 'field',
        text: '리포트 · 조치 이력 위의 숫자 카드가 대시보드 카드와 같은 모양으로 바뀌었습니다.',
      },
      {
        kind: 'ui',
        part: 'field',
        text: '고장 조치 이력의 조치 완료 상태 이름을 해결 완료로 바꿨습니다.',
      },
      {
        kind: 'ui',
        part: 'field',
        text: '주요 누적 이슈의 자동 문장을 더 자연스럽게 다듬었습니다.',
      },
    ],
  },
  {
    date: '2026-10-27',
    items: [
      {
        kind: 'new',
        icon: 'map-pin',
        part: 'business',
        text: '사업 대시보드 권역별 현황에 지도가 생겼습니다. 시도마다 숫자가 많을수록 큰 원이 표시되고, 지역을 누르면 그 땅이 살짝 떠오르며 옆에 그 지역의 문의, 데모, 계약, 설치 숫자가 나옵니다. 마우스 휠이나 + − 단추로 확대하고 축소할 수 있습니다. 지도에서 지역을 고르면 옆 표에서도 그 줄이 강조되고, 표의 줄을 눌러도 지도에서 그 지역이 선택됩니다. 지도 오른쪽과 아래의 손잡이를 끌면 지도 크기를 바꿀 수 있고, 손잡이를 두 번 누르면 처음 크기로 돌아옵니다.',
      },
      {
        kind: 'new',
        icon: 'layers',
        part: 'field',
        text: '필드 대시보드에 로봇 운영 이슈 칸이 생겼습니다. 메일로 받던 로봇 운영 이슈 리포트처럼 10월 상반기, 10월 하반기 단위로 전체 이슈와 로봇 이슈, 로봇 외 이슈를 세고, 바로 앞 기간과 지난달 같은 기간에 견준 요약 표를 모델마다 나눠 보여 줍니다. 15일 단위 추이 그래프와 조치 현황, 반복 발생 매장, 건별 표도 함께 나오고, 그래프의 선을 누르면 그 선만 떠올라 숫자를 따로 모아 보여 주고, 그 선의 점을 한 번 더 누르면 그 기간으로 바뀝니다. 빈 곳을 누르면 원래대로 돌아갑니다. 건별 표에는 와이어/로더나 RPI 같은 이슈 유형이 붙고, 앞선 석 달에 없던 유형은 신규 이슈로, 계속 나는 유형은 주요 누적 이슈로 따로 모아 보여 줍니다.',
      },
      {
        kind: 'new',
        icon: 'plus',
        part: 'field',
        text: "'리포트 · 조치 이력'의 고장 조치 이력에 새 조치 이력 단추가 생겼습니다. 캘린더 일정이나 고객사 리포트가 없어도 조치 기록을 바로 쓸 수 있습니다.",
      },
      {
        kind: 'new',
        icon: 'filter',
        part: 'field',
        text: '고장 조치 이력 표에 구분 칸이 생겼습니다. 증상과 조치 내용의 낱말을 보고 로봇 이슈인지 로봇 외 이슈인지 저절로 나누고, 연필을 눌러 직접 고칠 수도 있습니다.',
      },
      {
        kind: 'new',
        icon: 'file',
        part: 'field',
        text: '고장 조치 이력의 리포트 번호에 고객사 리포트로 등록되지 않은 번호도 적어 둘 수 있습니다. 노션에서 옮겨 온 기록에는 노션의 Problem No.가 들어가고, 리포트 번호로 검색할 수도 있습니다.',
      },
      {
        kind: 'ui',
        icon: 'grid',
        part: 'common',
        text: '구획별 테두리가 명확하지 않거나, 표 내용이 명확하게 읽히지 않던 문제를 개선했습니다.',
      },
      {
        kind: 'ui',
        icon: 'pen',
        part: 'common',
        text: '콘솔 곳곳의 안내 문구와 빈 화면 문구, 확인 창 문구를 사람이 쓴 것처럼 자연스러운 문장으로 다듬었습니다.',
      },
      {
        kind: 'ui',
        icon: 'wrench',
        part: 'field',
        text: "필드 대시보드의 미해결 리포트와 고장 조치 이력 숫자를 '리포트 · 조치 이력' 화면과 같은 기준으로 보여 줍니다. 등급이나 상태 막대를 누르면 그 화면이 그대로 걸러져 열리고, 리포트 화면에는 미해결만 보는 거르기가 생겼습니다. 숫자 카드도 사업 대시보드와 같은 모양으로 바꿨습니다.",
      },
      {
        kind: 'ui',
        icon: 'layers',
        part: 'business',
        text: '권역별 현황을 큰 칸 하나로 묶었습니다. 숫자 카드 아래에 권역별 표와 지도가 나란히 들어가고, 그 아래에 권역별 문의, 데모, 계약 그래프와 권역별 설치 대수 같은 그래프가 이어집니다. 달 선택은 권역별 현황 제목 바로 옆으로 옮겼습니다.',
      },
      {
        kind: 'ui',
        icon: 'grid',
        part: 'common',
        text: '대시보드의 숫자 카드를 작게 줄이고 팀 색으로 옅게 칠했습니다. 카드 아래 설명 문구는 없애고, 마우스를 올리면 보이게 했습니다. 사업 대시보드의 숫자 카드 6장은 한 줄로 모았습니다.',
      },
      {
        kind: 'ui',
        icon: 'truck',
        part: 'business',
        text: '사업 대시보드 맨 위 숫자 카드 줄의 활성 계약 핸드오프 카드 안에 설치 대기, 설치 완료, 회수 예정 칩을 넣고 따로 있던 칸은 없앴습니다. 설치 대기는 주황, 설치 완료는 초록으로 색을 갈랐습니다.',
      },
      {
        kind: 'ui',
        icon: 'grid',
        part: 'business',
        text: '지역별 설치 현황의 이름을 국내 설치 현황으로 바꾸고, 해외 설치 현황과 지역별 설치 그래프까지 세 칸을 한 줄에 놓았습니다. 표는 폭을 좁히고 줄마다 칠하던 바탕 대신 얇은 선으로 나눴습니다. 칸 제목 글씨도 조금 키웠습니다.',
      },
      {
        kind: 'ui',
        icon: 'list',
        part: 'business',
        text: '권역별 표를 보통 격자 표로 바꿨습니다. 머리글을 문의, 데모, 계약, 설치로 묶고, 묶음 사이와 권역 사이에는 진한 선을 그었습니다. 계약에도 비율 칸을 더했습니다.',
      },
      {
        kind: 'ui',
        icon: 'list',
        part: 'business',
        text: '권역별 현황의 표를 내용 폭에 맞게 좁히고 표 옆에 지도를 두었습니다. 다른 그래프는 아래 칸으로 옮겼고, 목표 카드도 작게 줄였습니다.',
      },
      {
        kind: 'ui',
        icon: 'pen',
        part: 'business',
        text: "권역별 현황의 입력 단추를 둘로 나눴습니다. 목표치는 권역별 현황의 '목표 입력'에서, 표의 숫자 보정은 권역별 표의 '보정 입력'에서 넣습니다. 두 단추에는 옅은 색을 깔아 눈에 잘 띄게 했습니다.",
      },
      {
        kind: 'ui',
        icon: 'list',
        part: 'business',
        text: '사업 대시보드의 마감 임박 계약 상세와 확인이 필요한 갱신 기록을 한 줄에 나란히 놓았습니다. 갱신 기록의 확정 단추는 이름 옆으로 올려 칸 높이를 줄였습니다.',
      },
      {
        kind: 'ui',
        part: 'business',
        text: '사업 대시보드의 지역별 설치 현황 표에 지역, 설치 대수, 고객사 열을 나눠 한눈에 읽히게 했습니다. 옆 그래프는 처음에 상위 8곳만 보이고, 아래 더보기 버튼을 누르면 나머지 지역도 펼쳐집니다.',
      },
      {
        kind: 'ui',
        icon: 'sparkles',
        part: 'common',
        text: '숫자 카드를 흰 바탕으로 바꾸고 이름 앞에 색 아이콘을 달았습니다.',
      },
      {
        kind: 'ui',
        icon: 'sparkles',
        part: 'business',
        text: '권역별 현황의 문의, 데모, 계약, 계약 대수, 설치 대수, 전환율 카드 이름 앞에 색 아이콘을 달았습니다.',
      },
      {
        kind: 'ui',
        icon: 'scroll',
        part: 'common',
        text: '패치노트의 줄마다 앞에 내용에 맞는 아이콘을 넣었습니다.',
      },
      {
        kind: 'fix',
        icon: 'list',
        part: 'common',
        text: '마감 임박 계약 표처럼 머리글이 칸마다 끊겨 보이던 표들을 한 줄 띠로 이어지게 고쳤습니다.',
      },
      {
        kind: 'fix',
        icon: 'map-pin',
        part: 'business',
        text: "권역별 현황의 문의 건수를 사업팀 엑셀과 같게 도입문의 현황 수기 탭에서 세고, 지역은 '지역2' 칸으로 나눕니다. '강남구', '수원시'처럼 시군구로 적혀 있거나 'Gangnam-gu'처럼 영어로 적혀 있어도 맞는 시도로 넣고, 국내 지명이 아닌 영어 지명은 해외로 셉니다. '중구'처럼 여러 시도에 있는 이름은 지역1 칸을 보고 정합니다.",
      },
      {
        kind: 'fix',
        icon: 'scroll',
        part: 'common',
        text: '변경 이력에서 스페어 표시와 권역별 현황 입력이 영어 코드 대신 한국어 이름으로 보이게 고쳤습니다.',
      },
      {
        kind: 'fix',
        icon: 'map-pin',
        part: 'business',
        text: '권역별 현황 지도의 보기 단추와 목표 입력, 보정 입력 단추를 눌렀다 떼면 검은 테두리가 남던 것을 고쳤습니다.',
      },
    ],
  },
  {
    date: '2026-10-26',
    items: [
      {
        kind: 'new',
        icon: 'list',
        part: 'business',
        text: '도입 문의 현황과 데모-계약 전환 표에서 마우스로 끌어 여러 칸을 한 번에 선택할 수 있습니다. Shift를 누른 채 클릭하거나 Shift와 방향키를 함께 눌러도 선택 범위를 넓힐 수 있습니다.',
      },
      {
        kind: 'new',
        icon: 'copy',
        part: 'business',
        text: '여러 칸을 선택한 뒤 Ctrl+C를 누르면 표 모양 그대로 복사되어 구글 시트나 엑셀에 바로 붙여 넣을 수 있습니다. Delete를 누르면 선택한 칸의 내용이 한 번에 지워집니다. 잠긴 칸은 지워지지 않습니다.',
      },
      {
        kind: 'new',
        part: 'manufacturing',
        text: "로봇 배치 창에 '스페어(예비) 로봇으로 지급' 항목이 추가되었습니다. 계약 대수와 별도로 매장에 두는 로봇이라면 체크해 주세요. 체크한 로봇은 계약에 자동으로 배정되지 않으며, 이 항목은 고객 배치일 때만 보입니다.",
      },
      {
        kind: 'new',
        part: 'business',
        text: "고객사 상세의 로봇 정보 표에서 이미 배치된 로봇을 스페어로 지정하거나 해제할 수 있습니다. 스페어로 지정한 로봇은 번호 옆에 '스페어' 표시가 붙고, 로봇 상세의 출고 이력에서도 확인할 수 있습니다.",
      },
      {
        kind: 'ui',
        part: 'business',
        text: '사업 대시보드의 국내/해외 설치 대수와 지역별 표에서 스페어 로봇은 빠집니다. 사업팀 권역별 현황 엑셀과 같은 기준으로 계약 대수만 셉니다.',
      },
      {
        kind: 'new',
        part: 'common',
        text: '대시보드 제목 옆에 사업 대시보드, 제조 대시보드, 필드 대시보드로 바로 이동하는 버튼이 생겼습니다.',
      },
      {
        kind: 'ui',
        part: 'common',
        text: '여러 화면의 제목 아래에 붙어 있던 설명 문구를 없애 화면을 간결하게 정리했습니다.',
      },
      {
        kind: 'new',
        part: 'business',
        text: '사업 대시보드에 권역별 현황이 추가되었습니다. 사업팀 권역별 현황 엑셀처럼 달마다 문의, 데모, 계약, 설치 숫자를 시도와 권역별로 모아 표와 그래프로 보여 줍니다. 문의와 데모는 인바운드 시트에서, 계약과 설치는 콘솔 기록에서 자동으로 셉니다.',
      },
      {
        kind: 'new',
        part: 'business',
        text: "권역별 현황에서 '목표 · 보정 입력'을 누르면 그달의 목표치를 넣거나 표의 숫자를 직접 고칠 수 있습니다. 목표를 넣으면 달성률이 함께 표시되고, 직접 고친 칸은 노란색으로 보입니다. 칸을 비우면 자동으로 센 숫자로 돌아갑니다.",
      },
    ],
  },
  {
    date: '2026-10-23',
    items: [
      {
        kind: 'new',
        part: 'business',
        text: "BUSINESS 메뉴에 인바운드 현황(자동), 도입 문의 현황(수기), 데모-계약 전환 화면이 추가되었습니다. 구글 시트 '인바운드 문의 현황'과 연결되어 있어서 콘솔에서 내용을 고치거나 줄을 추가하면 1분 안에 시트에 반영되고, 시트에서 고친 내용도 콘솔에 반영됩니다. 이 화면들은 제조팀에는 보이지 않습니다.",
      },
      {
        kind: 'new',
        part: 'business',
        text: '도입 문의 현황에서 문의 일자, 통화 날짜, 시연 예정일, 시연 완료일 칸을 누르면 달력이 열립니다. 문의 일자를 고르면 주차와 요일이 자동으로 채워지고, 유입 경로와 도입 목적, 가볼 만한 도입 매장 안내는 목록에서 고를 수 있습니다. 표 위쪽에는 시트에 있는 목표, 주차별 현황, 연락 여부, 권역, 담당자 요약이 함께 표시됩니다.',
      },
      {
        kind: 'ui',
        part: 'common',
        text: '왼쪽 메뉴가 기본적으로 접힌 상태로 아이콘만 보입니다. 위쪽 로고를 누르면 메뉴가 펼쳐지고, 다시 누르면 접힙니다.',
      },
      {
        kind: 'new',
        part: 'business',
        text: "데모-계약 전환과 도입 문의 현황 표에서 Ctrl+Z를 누르면 방금 고친 내용을 되돌리고, Ctrl+Y를 누르면 다시 적용합니다. '+ 새 줄' 버튼은 표 왼쪽 위 건수 옆으로 옮겼습니다. 이 버튼을 누르면 검색창 자리에 '시트에 추가'와 '취소' 버튼이 나타납니다. 9월 이전 달 표의 맨 앞 번호 열은 없앴습니다.",
      },
      {
        kind: 'ui',
        part: 'common',
        text: '왼쪽 메뉴의 로고를 한 번도 눌러 보지 않았다면 로고가 깜빡입니다. 한 번 누르고 나면 더 이상 깜빡이지 않습니다.',
      },
      {
        kind: 'ui',
        part: 'business',
        text: '인바운드 현황과 도입 문의 현황을 처음 열면 이번 달 표가 먼저 보입니다. 다음 달 탭이 미리 만들어져 있어도 이번 달부터 보여 줍니다.',
      },
      {
        kind: 'ui',
        part: 'common',
        text: "통합 캘린더와 제조 캘린더에서 오늘 날짜 옆에 '오늘' 표시가 붙어 한눈에 알아볼 수 있습니다.",
      },
      {
        kind: 'new',
        part: 'business',
        text: '구글 시트의 다음 달 탭(자동 IB, 도입문의 현황)이 매달 말일에 자동으로 만들어집니다. 새 탭은 지난달 탭 바로 왼쪽에 머리글만 남긴 채 만들어지며, 몇 분 안에 콘솔에도 나타납니다. 이미 직접 만든 탭이 있으면 그대로 둡니다.',
      },
    ],
  },
  {
    date: '2026-10-22',
    items: [
      {
        kind: 'fix',
        part: 'field',
        text: "캘린더에서 회수 일정을 삭제하면 고객사의 '회수 예정' 상태도 함께 해제되어 현장 앱의 로봇 회수 탭에서 빠집니다. 고객사 상세 화면 위쪽에도 '회수 예정 취소' 버튼이 추가되었습니다.",
      },
      {
        kind: 'new',
        part: 'field',
        text: "통합 캘린더에 재계약 일정을 추가할 수 있습니다. '+ 일정 추가'에서 재계약을 고르고 기존 고객사를 선택하면 새 계약 건(예: 마루상회[26년 10월])이 자동으로 채워지며, 다른 고객사로 바꿀 수도 있습니다. 로봇마다 회수할지 계속 사용할지 고릅니다. 재계약 일정은 날짜가 지나도 저절로 끝나지 않습니다. 일정을 눌러 '재계약 완료'를 누르거나, 현장 앱에서 새 고객사 이름으로 납품확인서, 설치교육, 직접 작성 문서에 서명을 받으면 완료됩니다. 완료한 뒤에도 되돌릴 수 있으며, 되돌리면 로봇이 원래 계약으로 돌아갑니다.",
      },
      {
        kind: 'new',
        part: 'field',
        text: "캘린더의 유지보수 일정에서 로봇을 여러 대 고를 수 있습니다. 정비 오더는 로봇마다 따로 만들어지지만 캘린더에는 '한우마을 판교점 (2대)'처럼 일정 하나로 보이고, 고장 조치 이력에도 '시리얼 941, 943'처럼 한 줄로 남습니다. 일정을 옮기거나 메모를 고치거나 삭제하면 묶인 로봇 모두에 똑같이 적용됩니다.",
      },
      {
        kind: 'ui',
        part: 'field',
        text: "일정 시간은 오전/오후와 1시부터 12시 중에서 고르도록 바꿨습니다. '지우기' 버튼은 없앴고, 시간을 비우려면 첫 칸에서 '선택'을 고르면 됩니다.",
      },
      {
        kind: 'fix',
        part: 'field',
        text: '고장 조치 이력 표의 증상 요약과 조치 내용이 작성할 때 넣은 줄바꿈대로 표시됩니다.',
      },
      {
        kind: 'new',
        part: 'manufacturing',
        text: '로봇 배정 인박스에서 배정이 필요한 고객사를 설치 예정순, 최신순, 이름순, 상태순으로 정렬할 수 있습니다. 상태순은 배정 필요, 배정 중, 완료, 배정 불가 순서입니다.',
      },
      {
        kind: 'ui',
        part: 'common',
        text: '로봇 배정 인박스 양쪽 패널의 머리 부분에 메뉴 색을 넣었고, 고객사 상세의 인프라 발주 표와 로봇 정보 표의 머리글 높이를 낮춰 맞췄습니다.',
      },
      {
        kind: 'fix',
        part: 'common',
        text: "상단 검색 결과에 고객사와 지점 이름이 보이지 않던 문제와 알림 창에 '알림' 제목이 보이지 않던 문제를 고쳤습니다.",
      },
      {
        kind: 'fix',
        part: 'business',
        text: '고객사 상세의 모델별 계약 내역, 인프라 발주, 로봇 정보 표 위에 머리글 색 띠가 어긋나 보이던 문제를 고쳤습니다.',
      },
    ],
  },
  {
    date: '2026-10-21',
    items: [
      {
        kind: 'new',
        part: 'manufacturing',
        text: "허니컴로보틱스 소유 로봇(#14, #124-2 등)도 비욘드허니컴 고객사에 배치하고, 계약에 배정하고, 교체할 수 있습니다. 로봇 상세의 '배치', '계약에 배정', '계약에 연결' 버튼을 다시 쓸 수 있고, 교체 창, 캘린더의 교체 완료 창, 제조 캘린더의 교체 로봇 선택, 배정 인박스 후보에도 허니컴로보틱스 로봇이 함께 나옵니다. 이런 로봇은 이름 옆에 '허니컴로보틱스'라고 표시됩니다. 배치하거나 배정해도 로봇의 소유 법인은 허니컴로보틱스로 유지됩니다.",
      },
      {
        kind: 'new',
        part: 'business',
        text: "고객사 상세의 로봇 정보 표에서 이미 회수된 로봇에도 '되돌리기' 버튼이 생겼습니다. 잘못 회수한 로봇을 확인 창을 거쳐 다시 운영 중으로 되돌릴 수 있습니다. 이때 풀렸던 계약 배정과 종료된 고객사도 회수 전 상태로 돌아가고, 아직 처리하지 않은 회수 정비 오더는 취소됩니다.",
      },
      {
        kind: 'new',
        part: 'common',
        text: '표 머리글의 칸 경계를 끌어서 열 너비를 바꿀 수 있습니다. 경계를 두 번 누르면 원래 너비로 돌아가고, 바꾼 너비는 이 브라우저에 저장됩니다.',
      },
      {
        kind: 'ui',
        part: 'common',
        text: '상단 바와 표 머리글 색이 지금 보고 있는 메뉴(BUSINESS, MANUFACTURING, FIELD)의 색을 따라갑니다. 화면 제목을 키우고 상단 버튼들의 크기와 색을 통일했습니다.',
      },
      {
        kind: 'ui',
        part: 'common',
        text: '로봇 목록의 라이프사이클 영역에서 테두리 상자를 없애고 단계만 남겼습니다. 화면 배경색을 조금 더 밝게 하고, 표 머리글 칸 사이에 짧은 구분선을 넣었습니다.',
      },
      {
        kind: 'ui',
        part: 'manufacturing',
        text: "로봇 목록에서 긴 비고는 한 줄로 줄여 보여 주고, 누르면 전체 내용을 볼 수 있습니다. 필터는 두 줄로 고정해 윗줄에는 검색과 필터를, 아랫줄에는 정렬, 기준일, 지금 마감, 내보내기를 두었습니다. 색상 필터에 '전체 색상' 항목이 추가되었습니다.",
      },
      {
        kind: 'ui',
        part: 'common',
        text: '표와 요약 카드의 모서리를 조금 덜 둥글게 다듬고, 표 머리글 옆 스크롤바 자리도 머리글과 같은 색으로 맞췄습니다.',
      },
    ],
  },
  {
    date: '2026-10-20',
    items: [
      {
        kind: 'new',
        part: 'manufacturing',
        text: "직원 계정으로도 허니컴로보틱스 소유 로봇을 조회하고 수정할 수 있습니다. 로봇 목록, 상세, 검색에 나타나며 편집, 상태 정정, QR 스티커, 서비스 오더 등을 평소 권한대로 쓸 수 있습니다. 조회 전용 계정은 보기만 할 수 있습니다. 로봇 상세 위쪽에는 '허니컴로보틱스' 표시가 붙고, 고객사와 계약은 지금처럼 자기 법인 것만 보입니다.",
      },
      {
        kind: 'ui',
        part: 'manufacturing',
        text: '허니컴로보틱스 로봇을 고객사에 배치하거나 계약에 배정 또는 연결하려면 지금처럼 관리자에게 요청해야 합니다. 로봇 상세의 해당 버튼은 꺼져 있고, 마우스를 올리면 이유가 표시됩니다. 고객사 상세의 교체 창과 제조 캘린더의 교체 로봇 선택에도 고를 수 있는 같은 법인 로봇만 나옵니다.',
      },
      {
        kind: 'new',
        part: 'field',
        text: '통합 캘린더에 로봇 교체 일정을 추가할 수 있습니다. 일정 하나에 로봇을 여러 대 고를 수 있고, 일정을 눌러 열리는 교체 완료 창에서 로봇마다 새로 넣을 로봇을 골라 완료합니다. 일부만 먼저 완료해도 됩니다. 이미 끝난 교체도 마우스 오른쪽 버튼으로 실제 교체일 등을 수정하거나, 삭제해서 교체를 되돌릴 수 있습니다.',
      },
      {
        kind: 'new',
        part: 'business',
        text: "고객사 상세의 로봇 정보 표에 '되돌리기' 버튼이 생겼습니다. 잘못 배치한 로봇을 확인 창을 거쳐 배치 전 상태로 되돌리면 운영 중 목록에서 빠집니다. 교체로 들어간 로봇이라면 빠졌던 기존 로봇이 다시 운영 중으로 돌아갑니다.",
      },
      {
        kind: 'new',
        part: 'field',
        text: "회수 사유가 '고장 교체'이면 '리포트 · 조치 이력' 메뉴의 고장 조치 이력에 '조치 완료'로 기록이 남습니다.",
      },
      {
        kind: 'ui',
        part: 'common',
        text: "회수 장소의 'AS 센터'를 '공장'으로 합쳤습니다. 콘솔과 현장 앱 모두 적용됩니다.",
      },
      {
        kind: 'fix',
        part: 'business',
        text: '고객사 상세의 로봇 정보 표 머리글이 두 줄로 깨져 보이던 문제를 고쳤습니다.',
      },
      {
        kind: 'ui',
        part: 'field',
        text: "캘린더에서 일정을 추가하거나 수정할 때 시간을 '14시 30분'처럼 시와 분으로 고르도록 바꿨습니다. 분은 10분 단위입니다.",
      },
      {
        kind: 'ui',
        part: 'field',
        text: '구글 캘린더의 유지보수 일정 설명을 메모, 콘솔 링크, 주소와 지도 순서로 줄을 나눠 읽기 쉽게 정리했습니다.',
      },
      {
        kind: 'new',
        app: 'field',
        part: 'field',
        text: "현장 앱 서명 화면의 '그 밖에 받아 둔 문서'에 '정리' 버튼이 생겼습니다. 고객사를 골라 이 기기에 받아 둔 문서를 한 번에 지울 수 있습니다. 아직 올리지 않은 서명이나 미리 채운 값이 있는 고객사는 고를 수 없습니다.",
      },
      {
        kind: 'ui',
        part: 'common',
        text: "사이드바의 '서명 관리' 메뉴 이름을 '서명 관리 · 페리페럴'로 바꾸고, 사이드바 왼쪽 여백을 조금 줄였습니다.",
      },
      {
        kind: 'new',
        part: 'field',
        text: '통합 캘린더에서 일정을 마우스 오른쪽 버튼으로 누르면 수정하거나 삭제할 수 있습니다. 수정에서는 날짜와 시간을 바꿀 수 있고, 캘린더에서 추가한 유지보수 일정은 작업 메모도 고칠 수 있습니다. 삭제는 한 번 더 확인한 뒤 진행되며, 캘린더에서 추가한 유지보수를 삭제하면 서비스 오더와 고장 조치 이력도 함께 지워집니다.',
      },
      {
        kind: 'new',
        part: 'field',
        text: "서명 문서의 '미리 채우기'에 '페리페럴 리스트 받기' 버튼이 생겼습니다. 지금 채운 내용으로 2페이지만 이미지 한 장으로 저장합니다.",
      },
      {
        kind: 'ui',
        part: 'field',
        text: '서명 양식 디자이너에서 종이 위에 마우스를 올리고 Ctrl을 누른 채 휠을 굴리면, 마우스가 가리키는 곳을 중심으로 확대하거나 축소합니다.',
      },
      {
        kind: 'fix',
        part: 'field',
        text: '서명 양식 디자이너에서 확대한 뒤 종이를 좌우로 옮길 수 없던 문제를 고쳤습니다. 아래쪽 가로 스크롤바나 Shift+휠로 왼쪽 끝부터 오른쪽 끝까지 움직일 수 있습니다.',
      },
      {
        kind: 'new',
        part: 'field',
        text: '서명 양식 디자이너의 표에서 칸을 선택하면 오른쪽 아래에 작은 네모(채우기 핸들)가 나타납니다. 엑셀처럼 끌면 숫자는 11, 12, 13이나 1, 3, 5, 7처럼 이어지는 번호로 채워지고, 글자는 같은 내용이 반복해서 채워집니다. 칸의 입력 설정과 글자 모양은 바뀌지 않습니다.',
      },
      {
        kind: 'fix',
        part: 'field',
        text: '서명 문서의 미리 채우기, 서명 페이지, 현장 태블릿에서 작은 입력 칸이 표 칸보다 아래로 밀려 보이던 문제를 고쳤습니다. 모바일과 태블릿에서 칸을 누르면 아래 칸에 입력되던 문제도 함께 고쳐, 이제 누른 칸에 정확히 입력됩니다.',
      },
      {
        kind: 'ui',
        part: 'field',
        text: '캘린더에서 추가한 유지보수 일정은 구글 캘린더 설명에 작업 메모가 적히고, 콘솔 링크를 누르면 고장 조치 이력의 해당 줄로 이동합니다.',
      },
      {
        kind: 'ui',
        part: 'field',
        text: "서명 양식 디자이너의 표 속성 메뉴를 정리했습니다. '칸 종류'는 행과 열 설정 바로 아래로, '정렬'은 테두리 설정 아래로 옮기고 '표 전체 기본' 항목은 없앴습니다. 글자 칸을 입력 칸으로 바꾸면 가운데 정렬로 시작합니다.",
      },
      {
        kind: 'fix',
        part: 'field',
        text: '서명 문서의 표 안 입력 칸 글씨가 칸 높이에 맞춰 지나치게 작아지던 문제를 고쳤습니다. 이제 미리 채우기, 이미지, 서명 페이지, 태블릿, PDF 모두에서 표에 지정한 글자 크기로 보입니다.',
      },
    ],
  },
  {
    date: '2026-10-19',
    items: [
      {
        kind: 'new',
        part: 'common',
        text: "새 버전이 배포되면 이미 열어 둔 화면에서도 '패치노트' 버튼이 깜빡이고, 옆에 '새 버전 · 새로고침' 버튼이 나타납니다. 이 버튼을 누르면 새 버전으로 바뀌므로 F5를 누르라고 따로 안내하지 않아도 됩니다.",
      },
      {
        kind: 'new',
        part: 'field',
        text: '통합 캘린더에서 날짜나 날짜 칸의 빈 곳을 누르면 일정을 추가할 수 있습니다. 로봇 설치, 그릴러 발주, 그릴러 납품, 유지보수, 회수 일정을 추가할 수 있으며, 추가한 일정은 1분 안에 구글 캘린더에도 등록됩니다.',
      },
      {
        kind: 'new',
        part: 'field',
        text: "통합 캘린더에 회수 일정이 표시됩니다. 일정을 끌어서 다른 날로 옮길 수 있고, 구글 캘린더 'BH_Robot Installation & Maintenance'와 서로 동기화됩니다.",
      },
      {
        kind: 'new',
        part: 'field',
        text: '일정 추가 창에서 시간을 선택할 수 있습니다. 시간을 고르면 구글 캘린더에 그 시각부터 1시간짜리 일정으로, 비워 두면 종일 일정으로 등록됩니다. 구글 캘린더에서 시간을 바꾸면 콘솔에도 반영됩니다.',
      },
      {
        kind: 'new',
        part: 'field',
        text: "캘린더에서 추가한 유지보수 일정은 '리포트 · 조치 이력' 메뉴의 고장 조치 이력에 '진행 중' 이슈로 함께 등록됩니다. 증상 요약에는 등록할 때 적은 메모만 들어갑니다. 캘린더에서 유지보수 일정을 누르면 해당 이력으로 바로 이동합니다.",
      },
      {
        kind: 'ui',
        part: 'common',
        text: '왼쪽 메뉴에서 선택한 항목이 메뉴 묶음의 색으로 표시됩니다. Business는 파란색, Manufacturing은 주황색, Field는 청록색, Admin은 보라색입니다.',
      },
      {
        kind: 'ui',
        part: 'field',
        text: '캘린더의 유지보수 일정에 서비스 오더 번호 대신 고객사 이름이 표시됩니다. 하루 안의 일정은 종일 일정이 먼저 나오고, 그다음 시간 순서로 정렬됩니다.',
      },
      {
        kind: 'fix',
        part: 'field',
        text: '캘린더에서 추가한 유지보수 일정이 캘린더에 보이지 않던 문제를 고쳤습니다.',
      },
      {
        kind: 'ui',
        part: 'field',
        text: '인프라 발주 표의 머리글(고객사, 위치, 담당자, 가스, 설치 예정 등)을 누르면 정렬됩니다. 한 번 더 누르면 반대 순서로, 세 번째 누르면 정렬이 해제됩니다.',
      },
      {
        kind: 'ui',
        part: 'field',
        text: '일정 추가 창의 고객사와 인프라 발주 칸에서 이름이나 계약 번호를 입력해 검색할 수 있습니다.',
      },
      {
        kind: 'ui',
        part: 'common',
        text: "메뉴 이름을 '캘린더 · 인프라 발주', '리포트 · 조치 이력', '추적 · 분석'으로 바꿨습니다.",
      },
      {
        kind: 'ui',
        part: 'common',
        text: '왼쪽 메뉴의 Business, Manufacturing, Field 묶음을 구분선과 굵은 제목으로 더 분명하게 나눴습니다.',
      },
      {
        kind: 'ui',
        part: 'field',
        text: '통합 캘린더의 색을 다듬었습니다. 설치 완료는 파란색, 회수는 진분홍색으로 표시됩니다.',
      },
      {
        kind: 'new',
        part: 'common',
        text: "상단에 '패치노트' 버튼이 생겼습니다. 새 업데이트가 있으면 버튼이 깜빡이고, 업데이트 내용을 날짜별로 나눠 보여 줍니다.",
      },
    ],
  },
  {
    date: '2026-10-09',
    items: [
      {
        kind: 'ui',
        part: 'manufacturing',
        text: "회계용 기준일 CSV 파일의 항목 28개를 모두 한국어로 바꿨습니다. 파일 이름은 '로봇목록_YYYY-MM-DD_기준.csv' 형식입니다.",
      },
    ],
  },
  {
    date: '2026-10-08',
    items: [
      {
        kind: 'new',
        part: 'manufacturing',
        text: '로봇 화면에서 기준일을 지정해 검색하면 그날 마감 기준의 로봇 목록을 볼 수 있습니다. 마감은 매달 말일에 자동으로 이루어집니다.',
      },
      {
        kind: 'new',
        app: 'field',
        part: 'field',
        text: "현장 앱의 설치 서명 화면을 '오늘 요약'으로 새로 만들었습니다. 인프라 발주 캘린더의 오늘 설치 일정이 바로 보이고, 고객사별로 접고 펼쳐서 볼 수 있습니다.",
      },
      {
        kind: 'new',
        app: 'field',
        part: 'field',
        text: "현장 앱의 '서비스 오더' 탭을 '로봇 회수' 탭으로 바꿨습니다. 회수 사유, 처분 방법, 사진 같은 회수 처리 내용을 인터넷이 연결되지 않았을 때도 기기에 저장해 두었다가 연결되면 올립니다.",
      },
      {
        kind: 'new',
        app: 'field',
        part: 'field',
        text: "오늘 방문할 곳에 서명 받을 문서가 없으면 '콘솔에서 서명 만들기' 버튼으로 바로 이동할 수 있습니다.",
      },
      {
        kind: 'ui',
        app: 'field',
        part: 'field',
        text: "'재동기화' 버튼 이름을 '업로드'로 바꾸고, 서명 문서 화면에도 '업로드' 버튼을 추가했습니다. 사진은 카메라로 찍거나 갤러리에서 고를 수 있습니다.",
      },
      {
        kind: 'fix',
        app: 'field',
        part: 'field',
        text: '휴대폰에서 새 버전이 늦게 반영되던 문제를 고쳤습니다. 이제 앱으로 돌아오면 자동으로 새 버전으로 바뀝니다.',
      },
      {
        kind: 'new',
        part: 'field',
        text: '현장 앱에서 서명 관리로 넘어오면 해당 고객사가 미리 선택된 채로 새 서명 창이 열립니다.',
      },
    ],
  },
  {
    date: '2026-10-02',
    items: [
      {
        kind: 'fix',
        part: 'field',
        text: '서명 양식을 복사하면 표와 사진 배치도 함께 복사됩니다.',
      },
      {
        kind: 'ui',
        part: 'business',
        text: '고객사 마스터의 영업 담당, 보증 보험, 모델 칸도 머리글을 눌러 정렬할 수 있습니다.',
      },
    ],
  },
  {
    date: '2026-10-01',
    items: [
      {
        kind: 'ui',
        part: 'common',
        text: '고객사 마스터와 로봇 목록에서 표 머리글을 누르면 정렬됩니다. 한 번 더 누르면 반대 순서로, 세 번째 누르면 정렬이 해제됩니다.',
      },
      {
        kind: 'new',
        part: 'business',
        text: '고객사 서류 화면에서 상태별로 거르고 정렬할 수 있습니다. 선택한 고객사 이름이 표시되고, 국내와 해외로 나뉘어 제목이 붙습니다.',
      },
      {
        kind: 'fix',
        part: 'common',
        text: '새 탭을 열 때마다 다시 로그인해야 하던 문제를 고쳤습니다. 이제 브라우저에서 한 번만 로그인하면 됩니다.',
      },
      {
        kind: 'fix',
        part: 'field',
        text: '콘솔에서 일정 날짜를 옮겨도 구글 캘린더에 입력해 둔 시간이 그대로 유지됩니다.',
      },
      {
        kind: 'ui',
        part: 'field',
        text: "캘린더 칸의 '+N건 더'를 누르면 그날 일정이 모두 펼쳐집니다. 구글 캘린더 일정 설명에는 주소와 지도 링크가 들어갑니다.",
      },
    ],
  },
  {
    date: '2026-09-30',
    items: [
      {
        kind: 'new',
        part: 'field',
        text: '구글 캘린더와 양방향으로 연동됩니다. 콘솔의 일정이 구글 캘린더에 등록되고, 구글 캘린더에서 옮기거나 삭제한 일정은 콘솔에도 반영됩니다.',
      },
      {
        kind: 'new',
        part: 'field',
        text: '통합 캘린더의 일정을 끌어서 다른 날로 옮길 수 있습니다. 표와 캘린더 사이의 너비도 조절할 수 있습니다.',
      },
      {
        kind: 'fix',
        part: 'business',
        text: '고객사를 등록하거나 편집할 때 계약 없이 저장하면 희망 설치 일정과 로봇 Store ID가 사라지던 문제를 고쳤습니다.',
      },
      {
        kind: 'fix',
        part: 'field',
        text: '솔루션 추적에서 불판과 Store ID가 매장에 연결되지 않던 문제를 고쳤습니다.',
      },
    ],
  },
  {
    date: '2026-09-23',
    items: [
      {
        kind: 'new',
        part: 'field',
        text: '서명 서식 편집기에서 칸 선택, 복사, 방향키 이동, 여러 칸 한 번에 선택을 할 수 있고, 칸 안에 사진을 넣거나 엑셀 서식을 가져올 수 있습니다.',
      },
      {
        kind: 'new',
        part: 'field',
        text: '로봇을 정하지 않은 상태로 서명 문서를 만들고 나중에 로봇을 지정할 수 있습니다. 서명 관리에 검색 기능을 추가했습니다.',
      },
      {
        kind: 'fix',
        part: 'manufacturing',
        text: "로봇 상태 '기타'와 관련된 표시, 현장에 나가 있는 로봇 판정, 교체, 리콜 처리 오류를 바로잡았습니다.",
      },
    ],
  },
  {
    date: '2026-09-18',
    items: [
      {
        kind: 'new',
        part: 'manufacturing',
        text: "로봇 상태에 '기타'가 새로 추가되었습니다. 이제 '운영 중'은 고객사에서 실제로 서비스 중인 로봇만 뜻합니다.",
      },
    ],
  },
  {
    date: '2026-09-16',
    items: [
      {
        kind: 'new',
        part: 'common',
        text: '연락 요청과 할당 요청 알림이 슬랙으로도 전송됩니다. 보내는 이는 콘솔 봇입니다.',
      },
      {
        kind: 'new',
        part: 'field',
        text: "로봇 상세의 '연결하기'에서 리포트를 골라 연결할 수 있습니다.",
      },
      {
        kind: 'new',
        part: 'field',
        text: '고장 조치 이력에서 로봇 시리얼을 수정할 수 있고, 한 칸에 여러 대를 적어도 각각 인식합니다. 조치 상태는 고객사 리포트에도 반영됩니다.',
      },
      {
        kind: 'fix',
        part: 'common',
        text: '수정 창에서 마우스를 끌다가 창 바깥에서 손을 떼도 창이 닫히지 않습니다.',
      },
    ],
  },
];

/** 지금 판의 표지 — 맨 위 날짜와 그날 항목 수. 같은 날 항목을 더해도 다시 깜빡인다. */
export function patchNotesVersion(notes: PatchNoteDay[] = PATCH_NOTES): string {
  const top = notes[0];
  return top ? `${top.date}#${top.items.length}` : '';
}

const SEEN_KEY = 'kuun:patch-notes:seen';

export function readSeenPatchNotes(): string | null {
  try {
    return window.localStorage.getItem(SEEN_KEY);
  } catch {
    return null;
  }
}

export function markPatchNotesSeen(version: string = patchNotesVersion()): void {
  try {
    window.localStorage.setItem(SEEN_KEY, version);
  } catch {
    /* 사생활 보호 모드 등 — 다음에 또 깜빡일 뿐 */
  }
}
